  import { LitElement, html, svg } from 'lit';
export class RDHighlighter extends LitElement {
            createRenderRoot() { return this; }

            static properties = {
                isVisible: { type: Boolean },
                overlayRole: { type: String }, 
                isOverSelected: { type: Boolean },

                mouseX: { type: Number },
                mouseY: { type: Number },
                globalAimingAdd: { type: Boolean },
                toolbarLocked: { type: Boolean }, 

                x: { type: Number }, y: { type: Number }, w: { type: Number }, h: { type: Number },
                margins: { type: Array }, paddings: { type: Array },

                boxColor: { type: String },
                showBoxModelFills: { type: Boolean }, showResizeHandles: { type: Boolean },
                
                tagName: { type: String }, tagInfo: { type: String }, parentTagName: { type: String }, badges: { type: Array },

                showToolbar: { type: Boolean }, showDragHandle: { type: Boolean },
                showTagName: { type: Boolean }, showTagInfo: { type: Boolean }, showActions: { type: Boolean },
                
                // --- NEW: Dynamic Actions Array ---
                actions: { type: Array }, 

                showAddTop: { type: Boolean }, showAddRight: { type: Boolean },
                showAddBottom: { type: Boolean }, showAddLeft: { type: Boolean },
            };

            constructor() {
                super();
                this.isVisible = false;
                this.overlayRole = 'hover';
                this.isOverSelected = false;

                this.mouseX = null;
                this.mouseY = null;
                this.globalAimingAdd = false;
                this.toolbarLocked = false;

                this.x = 0; this.y = 0; this.w = 0; this.h = 0;
                this.margins = [0,0,0,0]; this.paddings = [0,0,0,0];
                
                this.boxColor = 'var(--primary-color)';
                this.showBoxModelFills = false; this.showResizeHandles = false;
                this.showToolbar = true; this.showDragHandle = true; this.showActions = true;
                this.showTagName = true; this.showTagInfo = true;
                this.actions = []; // Empty by default
                
                this.tagName = ''; this.tagInfo = ''; this.parentTagName = ''; this.badges = [];
                
                this.showAddTop = false; this.showAddRight = false;
                this.showAddBottom = false; this.showAddLeft = false;
            }

            _fireEvent(name, detail = {}) {
                this.dispatchEvent(new CustomEvent(`lbi-${name}`, { detail, bubbles: true, composed: true }));
            }

            _getToolbarWidth() {
                if (this.renderRoot) {
                    const tb = this.renderRoot.querySelector('.lbi-toolbar') || this.renderRoot.querySelector('.lbi-hover-label');
                    if (tb && tb.offsetWidth) return tb.offsetWidth;
                }
                
                let width = 0;
                if (this.overlayRole === 'hover') {
                    width = 20 + (this.tagName ? this.tagName.length * 8 : 0) + (this.tagInfo ? Math.min(this.tagInfo.length * 7, 120) : 0);
                } else {
                    width = 8;
                    if (this.showDragHandle) width += 26;
                    if (this.tagName) width += this.tagName.length * 8 + 18;
                    if (this.tagInfo) width += Math.min(this.tagInfo.length * 7, 120) + 8;
                    if (this.badges) width += this.badges.length * 36;
                    
                    // Dynamically calculate width based on the number of action buttons
                    if (this.showActions && this.actions) {
                        width += this.actions.length * 28;
                    }
                }
                return Math.max(40, width);
            }

            _calculateToolbarPosition() {
                let labelTop = this.y - (this.overlayRole === 'hover' ? 30 : 34); 
                let labelLeft = this.x;
                
                const actualWidth = this._getToolbarWidth();
                
                if (labelTop < 56) {
                    labelTop = Math.max(60, this.y + 16); 
                }
                
                if (labelLeft < 4) labelLeft = 4;
                const maxLeft = window.innerWidth - actualWidth - 4;
                if (labelLeft > maxLeft) labelLeft = maxLeft;

                return { top: labelTop, left: labelLeft, width: actualWidth };
            }

            // --- Detect if mouse is actively aiming for an Add button ---
            isAimingAtAddBtn() {
                if (!this.isVisible || this.mouseX === null || this.mouseY === null) return false;
                
                const centerX = this.x + this.w / 2;
                const centerY = this.y + this.h / 2;
                const offset = 16; 
                const minCenterDistX = 40; 
                const minCenterDistY = 30;

                const offsetY = Math.max(offset, minCenterDistY - (this.h / 2));
                const offsetX = Math.max(offset, minCenterDistX - (this.w / 2));

                const topPos = { top: this.y - offsetY, left: centerX };
                const bottomPos = { top: this.y + this.h + offsetY, left: centerX };
                const leftPos = { top: centerY, left: this.x - offsetX };
                const rightPos = { top: centerY, left: this.x + this.w + offsetX };

                const radius = 32; 

                if (this.showAddTop && Math.hypot(this.mouseX - topPos.left, this.mouseY - topPos.top) < radius) return true;
                if (this.showAddBottom && Math.hypot(this.mouseX - bottomPos.left, this.mouseY - bottomPos.top) < radius) return true;
                if (this.showAddLeft && Math.hypot(this.mouseX - leftPos.left, this.mouseY - leftPos.top) < radius) return true;
                if (this.showAddRight && Math.hypot(this.mouseX - rightPos.left, this.mouseY - rightPos.top) < radius) return true;

                return false;
            }

            // --- Pure geometric check for Toolbar hovering ---
            _checkToolbarGeometry() {
                if (!this.isVisible || !this.showToolbar || this.mouseX === null || this.mouseY === null) return false;
                
                if (this.overlayRole === 'hover' && this.isOverSelected) return false;

                const tbPos = this._calculateToolbarPosition();
                
                return (this.mouseX >= tbPos.left - 5 && this.mouseX <= tbPos.left + tbPos.width + 5 &&
                        this.mouseY >= tbPos.top - 5 && this.mouseY <= tbPos.top + 33);
            }

            // --- Detect if mouse is directly hovering the Toolbar (with intent overrides) ---
            isHoveringToolbar() {
                if (this.globalAimingAdd) return false;
                return this._checkToolbarGeometry();
            }

            // --- Magnetic Tool Lock Logic ---
            isAimingAtTool() {
                if (!this.isVisible || this.mouseX === null || this.mouseY === null) return false;
                
                // 1. Check if aiming at top-left selected toolbar/label
                const tbPos = this._calculateToolbarPosition();
                if (this.mouseX >= tbPos.left - 20 && this.mouseX <= tbPos.left + tbPos.width + 20 &&
                    this.mouseY >= tbPos.top - 20 && this.mouseY <= tbPos.top + 40) {
                    return true;
                }

                // 2. Check if aiming at active + Add Buttons (Obeying toolbar lock intent)
                if (!this.toolbarLocked && this.isAimingAtAddBtn()) {
                    return true;
                }

                return false; 
            }

            _createHollowPath(outer, inner) {
                return `M ${outer.x} ${outer.y} h ${outer.w} v ${outer.h} h -${outer.w} Z
                        M ${inner.x} ${inner.y} h ${inner.w} v ${inner.h} h -${inner.w} Z`;
            }

            _renderBoxes() {
                const r = { x: this.x, y: this.y, w: this.w, h: this.h };
                const m = this.margins || [0,0,0,0];
                const p = this.paddings || [0,0,0,0];

                const marginBox = { x: r.x - m[3], y: r.y - m[0], w: r.w + m[3] + m[1], h: r.h + m[0] + m[2] };
                const paddingBox = { x: r.x, y: r.y, w: r.w, h: r.h }; 
                const contentBox = { x: r.x + p[3], y: r.y + p[0], w: Math.max(0, r.w - p[3] - p[1]), h: Math.max(0, r.h - p[0] - p[2]) };

                let fills = svg``;
                if (this.showBoxModelFills && this.overlayRole === 'hover') {
                    fills = svg`
                        <path d="${this._createHollowPath(marginBox, r)}" fill="rgba(207, 142, 109, 0.15)" fill-rule="evenodd" pointer-events="none" />
                        <path d="${this._createHollowPath(paddingBox, contentBox)}" fill="rgba(106, 171, 115, 0.15)" fill-rule="evenodd" pointer-events="none" />
                        <rect x="${contentBox.x}" y="${contentBox.y}" width="${contentBox.w}" height="${contentBox.h}" fill="rgba(var(--primary-rgb, 0, 123, 255), 0.15)" pointer-events="none" />
                    `;
                }

                let resizeNodes = svg``;
                if (this.showResizeHandles && this.overlayRole === 'selected') {
                    const s = 7, hs = s / 2;
                    const ht = 16, hths = ht / 2; // transparent hit-area size
                    const mx = r.x + r.w / 2, my = r.y + r.h / 2;
                    // Helper: invisible large hit rect behind each visual handle
                    const _hit = (cx, cy, cursor, handle) => svg`
                        <rect x="${cx - hths}" y="${cy - hths}" width="${ht}" height="${ht}"
                            fill="transparent" style="cursor: ${cursor};"
                            @mousedown="${(e) => this._fireEvent('resize-mousedown', { handle, nativeEvent: e })}"
                            @mouseup="${(e) => this._fireEvent('resize-mouseup', { handle, nativeEvent: e })}" />
                    `;
                    resizeNodes = svg`
                        <g pointer-events="auto">
                            <!-- Corner handles (visual + hit) -->
                            <rect x="${r.x - hs}" y="${r.y - hs}" width="${s}" height="${s}" fill="#ffffff" stroke="${this.boxColor}" stroke-width="1.5" style="cursor: nwse-resize;" pointer-events="none" />
                            ${_hit(r.x, r.y, 'nwse-resize', 'nw')}

                            <rect x="${r.x + r.w - hs}" y="${r.y - hs}" width="${s}" height="${s}" fill="#ffffff" stroke="${this.boxColor}" stroke-width="1.5" style="cursor: nesw-resize;" pointer-events="none" />
                            ${_hit(r.x + r.w, r.y, 'nesw-resize', 'ne')}

                            <rect x="${r.x - hs}" y="${r.y + r.h - hs}" width="${s}" height="${s}" fill="#ffffff" stroke="${this.boxColor}" stroke-width="1.5" style="cursor: nesw-resize;" pointer-events="none" />
                            ${_hit(r.x, r.y + r.h, 'nesw-resize', 'sw')}

                            <rect x="${r.x + r.w - hs}" y="${r.y + r.h - hs}" width="${s}" height="${s}" fill="#ffffff" stroke="${this.boxColor}" stroke-width="1.5" style="cursor: nwse-resize;" pointer-events="none" />
                            ${_hit(r.x + r.w, r.y + r.h, 'nwse-resize', 'se')}

                            <!-- Edge handles (visual + hit) -->
                            <rect x="${mx - hs}" y="${r.y - hs}" width="${s}" height="${s}" fill="#ffffff" stroke="${this.boxColor}" stroke-width="1.5" style="cursor: n-resize;" pointer-events="none" />
                            ${_hit(mx, r.y, 'n-resize', 'n')}

                            <rect x="${mx - hs}" y="${r.y + r.h - hs}" width="${s}" height="${s}" fill="#ffffff" stroke="${this.boxColor}" stroke-width="1.5" style="cursor: s-resize;" pointer-events="none" />
                            ${_hit(mx, r.y + r.h, 's-resize', 's')}

                            <rect x="${r.x - hs}" y="${my - hs}" width="${s}" height="${s}" fill="#ffffff" stroke="${this.boxColor}" stroke-width="1.5" style="cursor: w-resize;" pointer-events="none" />
                            ${_hit(r.x, my, 'w-resize', 'w')}

                            <rect x="${r.x + r.w - hs}" y="${my - hs}" width="${s}" height="${s}" fill="#ffffff" stroke="${this.boxColor}" stroke-width="1.5" style="cursor: e-resize;" pointer-events="none" />
                            ${_hit(r.x + r.w, my, 'e-resize', 'e')}
                        </g>
                    `;
                }

                const strokeWidth = this.overlayRole === 'hover' ? (this.isOverSelected ? '0' : '1') : '1.5';
                const strokeColor = this.isOverSelected ? 'transparent' : this.boxColor;
                
                return svg`
                    ${fills}
                    <rect x="${r.x}" y="${r.y}" width="${r.w}" height="${r.h}" fill="none" stroke="${strokeColor}" stroke-width="${strokeWidth}" pointer-events="none" />
                    ${resizeNodes}
                `;
            }

            _renderToolbar() {
                if (!this.showToolbar) return html``;

                const tbPos = this._calculateToolbarPosition();
                const zIndex = this.overlayRole === 'hover' ? 950 : 1000; 

                let identifierHtml = '';
                if (this.showTagInfo && this.tagInfo) {
                    let cls = 'lbi-syn-cls'; 
                    if (this.tagInfo.startsWith('#')) cls = 'lbi-syn-id'; 
                    else if (this.tagInfo.startsWith('[')) cls = 'lbi-syn-attr'; 
                    identifierHtml = html`<span class="${cls} lbi-truncate" title="${this.tagInfo}">${this.tagInfo}</span>`;
                }

                const hasTag = this.showTagName && !!this.tagName;
                const hasInfo = !!identifierHtml;
                const hasContent = hasTag || hasInfo;

                if (this.overlayRole === 'hover') {
                    if (!hasContent) return html``;
                    return html`
                        <div class="lbi-hover-label" style="top: ${tbPos.top}px; left: ${tbPos.left}px; z-index: ${zIndex};">
                            ${hasTag ? html`<span class="lbi-hover-tag">${this.tagName}</span>` : ''}
                            ${hasTag && hasInfo ? html`<div class="lbi-hover-sep"></div>` : ''}
                            ${hasInfo ? identifierHtml : ''}
                        </div>
                    `;
                } else {
                    return html`
                        <div class="lbi-toolbar" style="top: ${tbPos.top}px; left: ${tbPos.left}px; z-index: ${zIndex};">
                            ${this.showDragHandle ? html`
                                <div class="lbi-drag" title="Drag to move" 
                                    @mousedown="${(e) => this._fireEvent('drag-mousedown', { nativeEvent: e })}"
                                    @mouseup="${(e) => this._fireEvent('drag-mouseup', { nativeEvent: e })}"
                                    @mousemove="${(e) => this._fireEvent('drag-mousemove', { nativeEvent: e })}"
                                    @click="${(e) => this._fireEvent('drag-click', { nativeEvent: e })}">
                                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><circle cx="9" cy="5" r="1.5"/><circle cx="9" cy="12" r="1.5"/><circle cx="9" cy="19" r="1.5"/><circle cx="15" cy="5" r="1.5"/><circle cx="15" cy="12" r="1.5"/><circle cx="15" cy="19" r="1.5"/></svg>
                                </div>
                            ` : ''}
                            
                            ${hasContent ? html`
                                <div class="lbi-info-section" style="${(!this.badges || !this.badges.length) && !this.showActions ? 'border-right: none; padding-right: 0; margin-right: 0;' : ''}"
                                    @mousedown="${(e) => this._fireEvent('info-mousedown', { nativeEvent: e })}"
                                    @mouseup="${(e) => this._fireEvent('info-mouseup', { nativeEvent: e })}"
                                    @mousemove="${(e) => this._fireEvent('info-mousemove', { nativeEvent: e })}"
                                    @click="${(e) => this._fireEvent('info-click', { nativeEvent: e })}">
                                    ${hasTag ? html`<span class="lbi-selected-tag-box" style="${!hasInfo ? 'margin-right: 0;' : ''}">${this.tagName}</span>` : ''}
                                    ${hasInfo ? identifierHtml : ''}
                                </div>
                            ` : ''}

                            ${this.badges ? this.badges.map(b => html`<span class="lbi-badge">${b}</span>`) : ''}

                            ${this.showActions && this.actions && this.actions.length ? html`
                                <div class="lbi-actions">
                                    ${this.actions.map(action => {
                                        let btnClass = 'lbi-btn';
                                        if (action.type === 'danger') btnClass += ' lbi-btn-danger';
                                        if (action.type === 'active') btnClass += ' lbi-btn-active';
                                        
                                        return html`
                                            <div class="${btnClass}" title="${action.title}" 
                                                @click="${(e) => this._fireEvent('action', { id: action.id, nativeEvent: e })}"
                                                @mousedown="${(e) => this._fireEvent('action-mousedown', { id: action.id, nativeEvent: e })}"
                                                @mouseup="${(e) => this._fireEvent('action-mouseup', { id: action.id, nativeEvent: e })}"
                                                @mousemove="${(e) => this._fireEvent('action-mousemove', { id: action.id, nativeEvent: e })}"
                                                .innerHTML="${action.icon}">
                                            </div>
                                        `;
                                    })}
                                </div>
                            ` : ''}
                        </div>
                    `;
                }
            }

            _renderAddButtons() {
                const centerX = this.x + this.w / 2;
                const centerY = this.y + this.h / 2;
                
                const offset = 16; 
                const minCenterDistX = 40; 
                const minCenterDistY = 30;

                const offsetY = Math.max(offset, minCenterDistY - (this.h / 2));
                const offsetX = Math.max(offset, minCenterDistX - (this.w / 2));

                const topPos = { top: this.y - offsetY, left: centerX, type: 'horiz' };
                const bottomPos = { top: this.y + this.h + offsetY, left: centerX, type: 'horiz' };
                const leftPos = { top: centerY, left: this.x - offsetX, type: 'vert' };
                const rightPos = { top: centerY, left: this.x + this.w + offsetX, type: 'vert' };

                let activeDir = null;

                // Stop blooming completely if user's intent is locked onto the toolbar
                if (!this.toolbarLocked && this.mouseX !== null && this.mouseY !== null) {
                    
                    const btnDists = {
                        top: Math.hypot(this.mouseX - topPos.left, this.mouseY - topPos.top),
                        bottom: Math.hypot(this.mouseX - bottomPos.left, this.mouseY - bottomPos.top),
                        left: Math.hypot(this.mouseX - leftPos.left, this.mouseY - leftPos.top),
                        right: Math.hypot(this.mouseX - rightPos.left, this.mouseY - rightPos.top)
                    };

                    const isMouseInsideElement = this.mouseX >= this.x && this.mouseX <= this.x + this.w && 
                                                 this.mouseY >= this.y && this.mouseY <= this.y + this.h;

                    let minScore = Infinity;
                    ['top', 'bottom', 'left', 'right'].forEach(dir => {
                        const isEnabled = dir === 'top' ? this.showAddTop : 
                                          dir === 'bottom' ? this.showAddBottom :
                                          dir === 'left' ? this.showAddLeft : 
                                          this.showAddRight;

                        if (isEnabled && btnDists[dir] < minScore) {
                            minScore = btnDists[dir];
                            activeDir = dir;
                        }
                    });

                    // THE FIX:
                    // If your mouse is inside the element, we use a generous 60px radius so it blooms easily as you approach the border.
                    // If your mouse leaves the element (e.g. to hover a neighbor), the radius instantly shrinks to a tight 32px.
                    // This guarantees they NEVER bloom while hovering neighboring UI, unless you point directly at the button itself!
                    const threshold = isMouseInsideElement ? 60 : 32;

                    if (minScore >= threshold) {
                        activeDir = null;
                    }
                }

                const renderBtn = (pos, dir, title) => html`
                    <div class="lbi-add-wrapper ${pos.type} ${activeDir === dir ? 'is-near' : ''}" style="top: ${pos.top}px; left: ${pos.left}px;">
                        <div class="lbi-add-btn" title="${title}"
                            @click="${(e) => this._fireEvent('add', { direction: dir, nativeEvent: e })}"
                            @mousedown="${(e) => this._fireEvent('add-mousedown', { direction: dir, nativeEvent: e })}"
                            @mouseup="${(e) => this._fireEvent('add-mouseup', { direction: dir, nativeEvent: e })}"
                            @mousemove="${(e) => this._fireEvent('add-mousemove', { direction: dir, nativeEvent: e })}">
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-linecap="round"><path d="M12 5v14M5 12h14"/></svg>
                            <span>Add</span>
                        </div>
                    </div>
                `;

                return html`
                    ${this.showAddTop ? renderBtn(topPos, 'top', 'Add Above') : ''}
                    ${this.showAddBottom ? renderBtn(bottomPos, 'bottom', 'Add Below') : ''}
                    ${this.showAddLeft ? renderBtn(leftPos, 'left', 'Add Left') : ''}
                    ${this.showAddRight ? renderBtn(rightPos, 'right', 'Add Right') : ''}
                `;
            }

            show() { this.isVisible = true; }
            hide() { this.isVisible = false; }

            render() {
                if (!this.isVisible) return html`<div class="lbi-hidden"></div>`;

                let overlayZ = this.overlayRole === 'hover' ? 10000 : 9990;

                if (this.isHoveringToolbar()) {
                    overlayZ = 10020;
                }

                return html`
                    <div class="lbi-overlay" style="pointer-events: none; z-index: ${overlayZ};">
                        <svg class="lbi-svg-canvas" style="pointer-events: none;">
                            ${this._renderBoxes()}
                        </svg>
                        ${!this.isOverSelected ? this._renderToolbar() : html``}
                        ${this._renderAddButtons()}
                    </div>
                `;
            }
        }
        customElements.define('rd-highlighter', RDHighlighter);